import React, { useState, useEffect } from 'react'
import { Box, Typography } from '@mui/material'
import { TextElement } from './types'

type DraggableTextProps = {
  element: TextElement
  scale: number
  isSelected: boolean
  onUpdate: (updates: Partial<TextElement>) => void
  onSelect: () => void
}

export default ({
  element,
  scale,
  isSelected,
  onUpdate,
  onSelect,
}: DraggableTextProps) => {
  let [isDragging, setIsDragging] = useState(false)
  let [dragStart, setDragStart] = useState({ x: 0, y: 0 })

  let handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    setDragStart({
      x: e.clientX - element.x * scale,
      y: e.clientY - element.y * scale,
    })
    onSelect()
    e.preventDefault()
  }

  let handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return

    let newX = (e.clientX - dragStart.x) / scale
    let newY = (e.clientY - dragStart.y) / scale

    onUpdate({ x: Math.max(0, newX), y: Math.max(0, newY) })
  }

  let handleMouseUp = () => {
    setIsDragging(false)
  }

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, dragStart, scale])

  return (
    <Box
      sx={{
        position: 'absolute',
        cursor: 'move',
        p: 0.5,
        border: isSelected ? 2 : 1,
        borderColor: isSelected ? 'primary.main' : 'rgba(25, 118, 210, 0.3)',
        bgcolor: isSelected ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
        borderStyle: isSelected ? 'solid' : 'dashed',
        userSelect: 'none',
        left: element.x * scale,
        top: element.y * scale,
        minWidth: 20,
        minHeight: 20,
        '&:hover': {
          bgcolor: 'rgba(25, 118, 210, 0.15)',
          borderColor: 'primary.main',
          borderStyle: 'solid',
        },
      }}
      onMouseDown={handleMouseDown}
      onClick={(e) => {
        e.stopPropagation()
        onSelect()
      }}
    >
      <Typography
        sx={{
          fontSize: element.fontSize * scale,
          fontWeight: element.bold ? 'bold' : 'normal',
          fontStyle: element.italic ? 'italic' : 'normal',
          textDecoration: element.underline ? 'underline' : 'none',
          m: 0,
          p: 0,
          lineHeight: 1,
          color: 'black',
        }}
      >
        {element.content}
      </Typography>
    </Box>
  )
}
